import { Link } from "react-router-dom";
import { Check, ChevronRight, LayoutGrid, List as ListIcon, CalendarDays } from "lucide-react";
import { t } from "@/i18n";

export function Breadcrumbs({ items, testId = "breadcrumbs" }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-1 text-sm text-slate-500" data-testid={testId}>
      {items.map(([label, to], i) => (
        <span key={i} className="inline-flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
          {to ? <Link to={to} className="hover:text-slate-900 hover:underline" data-testid={`${testId}-${i}`}>{label}</Link> : <span className="font-semibold text-slate-800">{label}</span>}
        </span>
      ))}
    </nav>
  );
}

/** Odoo-like status bar. `onPick` only fires for steps in `allowed` (server-provided next statuses). */
export function StatusBar({ steps, current, allowed = [], onPick, testPrefix = "status-step", idPrefix }) {
  const idx = steps.indexOf(current);
  return (
    <div className="w-full max-w-[calc(100vw-2rem)] overflow-x-auto rounded-lg border border-slate-200 bg-white lg:max-w-full" data-testid="status-bar">
    <ol className="flex min-w-full w-max text-xs font-semibold">
      {steps.map((s, i) => {
        const cur = s === current;
        const done = idx >= 0 && i < idx;
        const can = !!onPick && allowed.includes(s);
        return (
          <li key={s} className="min-w-[88px] flex-1">
            <button type="button" disabled={!can} onClick={() => onPick(s)} data-testid={idPrefix && can ? `${idPrefix}-${s}` : `${testPrefix}-${s}`} aria-current={cur ? "step" : undefined}
              className={`flex h-10 w-full items-center justify-center gap-1 whitespace-nowrap border-r border-slate-100 px-3 transition-colors duration-150 last:border-r-0
                ${cur ? "bg-[#0A2540] text-white" : done ? "text-emerald-700" : "text-slate-400"} ${can ? "cursor-pointer text-teal-700 hover:bg-teal-50" : "cursor-default"}`}>
              {done && <Check className="h-3.5 w-3.5" />}{t(`status.${s}`)}
            </button>
          </li>
        );
      })}
    </ol>
    </div>
  );
}

export function SmartButton({ icon: Icon, label, value, to, testId, tone = "" }) {
  const body = (
    <>
      {Icon && <Icon className="h-5 w-5 shrink-0 text-teal-600" />}
      <span className="min-w-0 text-left leading-tight"><span className={`block text-base font-bold ${tone}`}>{value ?? "—"}</span><span className="block truncate text-xs text-slate-500">{label}</span></span>
    </>
  );
  const cls = "flex min-h-14 items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2 transition-[border-color,box-shadow] duration-150 hover:border-teal-400 hover:shadow-sm";
  return to ? <Link to={to} className={cls} data-testid={testId}>{body}</Link> : <div className={cls} data-testid={testId}>{body}</div>;
}

export function SmartButtons({ children }) {
  return <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="smart-buttons">{children}</div>;
}

export function RecordHeader({ crumbs, eyebrow, title, subtitle, badge, actions, statusBar, smart }) {
  return (
    <div className="space-y-4 gf-rise" data-testid="record-header">
      <Breadcrumbs items={crumbs} />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <p className="font-mono text-sm font-semibold text-teal-700">{eyebrow}</p>}
          <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl" data-testid="page-title">{title}</h1>
          {subtitle && <div className="mt-1 text-sm text-slate-600">{subtitle}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-2">{badge}{actions}</div>
      </div>
      {statusBar}
      {smart}
    </div>
  );
}

export function QuickChips({ options, value, onChange, testPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testPrefix}-chips`}>
      {options.map(([v, label]) => (
        <button key={v || "all"} type="button" onClick={() => onChange(v)} data-testid={`${testPrefix}-${v || "all"}`}
          className={`min-h-9 rounded-full border px-3.5 py-1 text-xs font-semibold transition-colors duration-150 ${value === v ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{label}</button>
      ))}
    </div>
  );
}

const VIEW_ICONS = { list: ListIcon, kanban: LayoutGrid, calendar: CalendarDays };

export function ViewSwitcher({ views, current }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="view-switcher">
      {views.map(([key, to]) => {
        const Icon = VIEW_ICONS[key];
        const cls = `inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-semibold ${key === current ? "bg-[#0A2540] text-white" : "text-slate-600 hover:bg-slate-100"}`;
        return key === current ? <span key={key} className={cls} data-testid={`view-${key}`}><Icon className="h-4 w-4" />{t(`b13.views.${key}`)}</span>
          : <Link key={key} to={to} className={cls} data-testid={`view-${key}`}><Icon className="h-4 w-4" />{t(`b13.views.${key}`)}</Link>;
      })}
    </div>
  );
}
